import { claimCsrfLifecycle, refreshCsrfToken } from './csrfToken'

/**
 * PLT-2867 — refresh the CSRF token when a page comes back from somewhere it may
 * have outlived its session.
 *
 * THE FAILURE IT HEALS. A mobile browser drops `_spectora_session` and later
 * restores the tab, still holding the token of the session that is gone. The
 * tab's first write is refused, and under `:null_session` that refusal cannot
 * heal it (see `refreshCsrfToken` in `csrfToken.js`). Refreshing on the way back
 * means the first write already carries a live token.
 *
 * THREE TRIGGERS, because a restored tab arrives three different ways:
 *
 *   1. `pageshow` with `event.persisted`: a back/forward cache restore. The page's
 *      JavaScript resumes where it stopped and nothing reloads.
 *   2. `visibilitychange` back to `visible` after at least `LONG_HIDE_MS` hidden.
 *      A tab left in the background long enough for the browser to drop the
 *      session cookie. Ten minutes catches most of them and costs at most one GET
 *      per long absence, instead of one per tab switch.
 *   3. Boot, when the navigation entry says the document did not come from the
 *      network: `type === 'back_forward'`, or a cached body (`transferSize === 0`
 *      with `decodedBodySize > 0`). This is how iOS shows a tab restored after the
 *      app was killed: a cold load of cached HTML, which never fires a `persisted`
 *      `pageshow`.
 *
 * ONCE PER PAGE. `csrfBootstrap.js` is prepended to every webpack entry, so this
 * runs once per entry chunk. `claimCsrfLifecycle` keeps the flag on the page-level
 * slot, so the second and later chunks install nothing.
 *
 * Every browser API is guarded. In a context without `window`, `document` or the
 * Performance API it installs what it can and never throws.
 */
export const LONG_HIDE_MS = 10 * 60 * 1000

const bootedFromCache = () => {
  try {
    const entry = window.performance?.getEntriesByType?.('navigation')?.[0]

    if (!entry) {
      return false
    }

    if (entry.type === 'back_forward') {
      return true
    }

    return entry.transferSize === 0 && entry.decodedBodySize > 0
  } catch (error) {
    return false
  }
}

export const installCsrfLifecycleRefresh = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return false
  }

  if (!claimCsrfLifecycle()) {
    return false
  }

  try {
    window.addEventListener('pageshow', (event) => {
      if (event?.persisted) {
        refreshCsrfToken()
      }
    })

    // Timed from the last `hidden`, so a page that loads in a background tab
    // counts from its load.
    let hiddenAt = document.visibilityState === 'hidden' ? Date.now() : null

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now()
        return
      }

      if (document.visibilityState === 'visible' && hiddenAt !== null) {
        const hiddenFor = Date.now() - hiddenAt
        hiddenAt = null

        if (hiddenFor >= LONG_HIDE_MS) {
          refreshCsrfToken()
        }
      }
    })

    if (bootedFromCache()) {
      refreshCsrfToken()
    }
  } catch (error) {
    // A refresh is an optimisation. Failing to install one must not break the page.
  }

  return true
}

export default installCsrfLifecycleRefresh
